import React, { FormEvent, ReactElement, useCallback, useState } from 'react';
import { Button, Input, Select } from '@milanote/roma';
import { isEmpty } from 'lodash/fp';

import http from '../../utils/services/http';
import globalLogger from '../../logger';

import { AxiosError } from 'axios';
import { METHODS } from '../../../common/utils/http/httpConstants';
import {
    AppVersionRuleModifier,
    AppVersionRulePlatform,
    AppVersionRuleStatus,
} from '../../../common/appVersionRule/appVersionRuleTypes';

const logger = globalLogger.createChannel('admin.app-versions');

type AdminDashboardAppVersionsFormProps = {
    onComplete: () => void;
    url: string;
};

const platformItems = [
    { id: AppVersionRulePlatform.ios, label: 'iOS' },
    { id: AppVersionRulePlatform.android, label: 'Android' },
    { id: AppVersionRulePlatform.electron, label: 'Electron' },
    { id: AppVersionRulePlatform.web, label: 'Web / PWA' },
];

const modifierItems = [
    { id: AppVersionRuleModifier.olderOrEqual, label: '<=' },
    { id: AppVersionRuleModifier.equal, label: '==' },
    { id: AppVersionRuleModifier.newerOrEqual, label: '>=' },
];

const statusItems = [
    { id: AppVersionRuleStatus.blocked, label: 'Blocked' },
    { id: AppVersionRuleStatus.unsafe, label: 'Unsafe' },
];

export const AdminDashboardAppVersionsForm = ({
    onComplete,
    url,
}: AdminDashboardAppVersionsFormProps): ReactElement => {
    // form data
    const [platform, setPlatform] = useState(AppVersionRulePlatform.ios);
    const [ruleModifier, setRuleModifier] = useState(AppVersionRuleModifier.olderOrEqual);
    const [version, setVersion] = useState('');
    const [reason, setReason] = useState('');
    const [status, setStatus] = useState(AppVersionRuleStatus.blocked);

    // submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [error, setError] = useState('');

    const onSubmit = useCallback(
        async (event: FormEvent) => {
            event.preventDefault();

            setError('');
            setIsSubmitting(true);

            try {
                await http({
                    method: METHODS.POST,
                    url,
                    data: {
                        platform: platform,
                        modifier: ruleModifier,
                        version: version,
                        reason: reason,
                        status: status,
                    },
                });

                setIsSuccessful(true);
                setIsSubmitting(false);

                onComplete();
            } catch (err) {
                if (err instanceof AxiosError) {
                    setError(err.response?.data.error.message);
                } else if (err instanceof Error) {
                    setError(err.message);
                }
                logger.error('Failed to submit app version rule', err, { platform, ruleModifier, version, status });
                setIsSuccessful(false);
                setIsSubmitting(false);
            }
        },
        [onComplete, platform, reason, ruleModifier, status, url, version],
    );

    return (
        <form className="flex flex-col gap-md" onSubmit={onSubmit}>
            <div className="flex flex-col gap-md">
                <Select
                    label="Platform"
                    size="sm"
                    items={platformItems}
                    value={platform}
                    onChange={(key) => key && setPlatform(key as AppVersionRulePlatform)}
                >
                    {(item) => <Select.Item {...item} />}
                </Select>
                <Select
                    label="Modifier"
                    size="sm"
                    items={modifierItems}
                    value={ruleModifier}
                    onChange={(key) => key && setRuleModifier(key as AppVersionRuleModifier)}
                >
                    {(item) => <Select.Item {...item} />}
                </Select>
                <Input
                    size="sm"
                    label="App Version Rule to Add"
                    id="versionNumber"
                    name="versionNumber"
                    value={version}
                    onChange={setVersion}
                />
                <Input size="sm" label="Reason" id="reason" name="reason" value={reason} onChange={setReason} />
                <Select
                    label="Status"
                    size="sm"
                    items={statusItems}
                    value={status}
                    onChange={(key) => key && setStatus(key as AppVersionRuleStatus)}
                >
                    {(item) => <Select.Item {...item} />}
                </Select>
            </div>

            <div className="flex items-center gap-md">
                {!isSuccessful && (
                    <Button
                        type="submit"
                        color="primary"
                        size="md"
                        isDisabled={isEmpty(version) || isEmpty(reason)}
                        isLoading={isSubmitting}
                    >
                        Submit
                    </Button>
                )}
                {isSuccessful && <span className="success">✔︎ Rule added</span>}
                {error && <span className="error">{error}</span>}
            </div>
        </form>
    );
};

export default AdminDashboardAppVersionsForm;
